import { BASE_URL } from '@avanta/shared/constants/common';
import { service, type ServiceRequestConfig } from '@avanta/shared/services/api';
import {
  type AdminRole,
  type UpdateAdminEnterprisePayload,
} from '@avanta/shared/types/enterprises';
import { type User } from '@avanta/shared/types/users';
import { type AxiosResponse } from 'axios';
import {
  type AdminEnterprise,
  type CreateAdminEnterprisePayload,
  type CountAdminsByEnterprise,
  type CountEmployeesByEnterprise,
  type EnterpriseActivityItem,
  type EnterpriseContract,
  type EnterpriseInvoicesResponse,
  type GetEnterpriseActivityParams,
  type GetEnterpriseInvoicesParams,
} from 'types/enterprises';

const ENTERPRISES_URL = `${BASE_URL}enterprises`;

interface EnterprisesServiceTypes {
  getEmployeesByEnterprise: ({
    enterpriseId,
    offset,
    limit,
    search,
  }: {
    enterpriseId: string;
    offset?: number;
    limit?: number;
    search?: string;
  }) => Promise<AxiosResponse<User[]>>;
  getCountEmployeesByEnterprise: (
    enterpriseId: string
  ) => Promise<AxiosResponse<CountEmployeesByEnterprise>>;
  getAdminsByEnterprise: (
    enterpriseId: string,
    offset: number,
    limit: number,
    role?: AdminRole
  ) => Promise<AxiosResponse<AdminEnterprise[]>>;
  getCountAdminsByEnterprise: (
    enterpriseId: string,
    role?: AdminRole
  ) => Promise<AxiosResponse<CountAdminsByEnterprise>>;
  createAdmin: (payload: CreateAdminEnterprisePayload) => Promise<AxiosResponse<AdminEnterprise>>;
  editAdmin: (
    adminId: string,
    payload: UpdateAdminEnterprisePayload
  ) => Promise<AxiosResponse<AdminEnterprise>>;
  getInvoices: (
    insuranceId: string,
    params?: GetEnterpriseInvoicesParams
  ) => Promise<AxiosResponse<EnterpriseInvoicesResponse>>;
  getInvoicePdf: (insuranceId: string, invoiceId: number) => Promise<AxiosResponse<Blob>>;
  getEnterpriseContracts: (enterpriseId: string) => Promise<AxiosResponse<EnterpriseContract[]>>;
  getActivity: (
    enterpriseId: string,
    params?: GetEnterpriseActivityParams
  ) => Promise<AxiosResponse<EnterpriseActivityItem[]>>;
}

export const EnterprisesService: EnterprisesServiceTypes = {
  getEmployeesByEnterprise: async ({ enterpriseId, offset = 0, limit = 50, search }) =>
    service.get(`${ENTERPRISES_URL}/${enterpriseId}/employees`, {
      params: {
        offset,
        limit,
        search,
      },
    }),
  getCountEmployeesByEnterprise: async enterpriseId =>
    service.get(`${ENTERPRISES_URL}/${enterpriseId}/employees/count`),
  getAdminsByEnterprise: async (enterpriseId, offset = 0, limit = 10, role) =>
    service.get(`${ENTERPRISES_URL}/${enterpriseId}/admins`, {
      params: {
        offset,
        limit,
        ...(role ? { role } : {}),
      },
    }),
  getCountAdminsByEnterprise: async (enterpriseId, role) =>
    service.get(`${ENTERPRISES_URL}/${enterpriseId}/admins/count`, {
      params: {
        ...(role ? { role } : {}),
      },
    }),
  createAdmin: async payload =>
    service.post(`${ENTERPRISES_URL}/${payload.enterpriseId}/admins`, payload),
  editAdmin: async (adminId, payload) =>
    service.put(`${ENTERPRISES_URL}/${payload.enterpriseId}/admins/${adminId}/role`, payload),
  // TODO: Odoo only filters by NIF and date range; search/offset/limit are in-memory.
  getInvoices: async (insuranceId, params) => {
    const config: ServiceRequestConfig = {
      params,
      skipGlobalErrorHandler: true,
    };
    return service.get(`${ENTERPRISES_URL}/${insuranceId}/invoices`, config);
  },
  getInvoicePdf: async (insuranceId, invoiceId) => {
    const config: ServiceRequestConfig = {
      params: { forceDownload: true },
      responseType: 'blob',
      skipGlobalErrorHandler: true,
    };
    return service.get(`${ENTERPRISES_URL}/${insuranceId}/invoices/${invoiceId}/pdf`, config);
  },
  getEnterpriseContracts: async enterpriseId =>
    service.get(`${ENTERPRISES_URL}/insurances/${enterpriseId}/contracts`),
  getActivity: async (enterpriseId, params) =>
    service.get(`${ENTERPRISES_URL}/${enterpriseId}/activity`, {
      params: {
        limit: params?.limit,
        types: params?.types?.length ? params.types.join(',') : undefined,
      },
    }),
};
